import { useState } from 'react'

import type { BatchListRow } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { Pagination } from '../../atoms/Pagination.tsx'
import type { StateNoticeTone } from '../../atoms/StateNotice.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { useGroupTabLayout } from '../groupTabs.ts'
import { OPS_TABS, type OpsTabId } from '../ops/opsTabs.ts'
import { gate, type ScreenAccess } from '../access.ts'
import { DEMO_BATCH_HISTORY } from '../demoData.ts'
import { batchIdColumn } from '../batches/batchIdColumn.tsx'
import {
  batchOwnerColumn,
  ownerSearchTexts,
  type BatchOwners,
} from '../batches/batchOwnerColumn.tsx'
import { batchSampleCountColumn } from '../batches/batchSampleCountColumn.tsx'
import { batchStatusColumn } from '../batches/batchStatusColumn.tsx'
import {
  batchCreatedColumn,
  batchEndedColumn,
  batchStartedColumn,
} from '../batches/batchTimeColumns.tsx'
import { withPriority } from '../listColumnPriority.ts'
import type { ListPaging } from '../listPaging.ts'

import '../screens.css'

/**
 * 운영 › 이력 — 와이어프레임 `hist('batch')` 2356행.
 *
 * 끝난 작업(`completed` · `aborted`)만 **오래된 것부터** 보인다. 진행 중인 것은 운영 › 작업에 있다
 * (2026-09-15 지시로 두 화면이 갈렸다 · §9-2).
 *
 * 🔴 **탭 줄을 두지 않았다.** 이 화면의 탭은 하나뿐이고, eric 규칙이 "탭이 하나면 탭 줄 자체를
 *    두지 않는다"이다(§1-3 · 와이어프레임 2388행). 주 버튼(내보내기)은 검색 툴바 오른쪽 끝으로 간다.
 *
 * ⚠ **팀장 브리프의 「탭 6개 중 감사 추적 · 이벤트 · 보정 이력만 구현」은 따르지 않았다.**
 *    그 셋은 지금 와이어프레임에서 **다른 화면**에 있다 — 감사·이벤트는 시스템 › 로그,
 *    보정 이력은 모듈 상세 › 보정 탭이다(§9-2 · §18-1). 여기에 다시 만들면 히나가 맡은 화면과
 *    같은 것이 두 벌 생긴다. 근거는 docs/screens.md 「내가 정한 것」.
 *
 * 🔴 **보고서 열은 2026-09-30 eric 지시로 뺐다**(「지금 화면에서.. 보고서 부분은 빼줘」). 서버가 `batch_report` 를
 *    만들면 되살릴지 그때 정한다. 종전에는 행마다 「시료 CSV」 단추(`POST /exports` · `sample`/`csv`)가 있었고
 *    같은 단추가 서랍(작업 상세) 머리에도 섰다 — 둘 다 걷었다(`HistoryReportButtons` · `useHistoryReports` 삭제).
 */
/**
 * 처음 정렬 — **서버 차례와 같다**(`GET /batches?phase=finished` = `completed_at desc nulls last` · `_order`).
 * 🔴 **머리글 정렬은 받은 쪽 안에서만이다** — 까닭 · 서버 요청은 `BatchesScreen` 의 `BATCHES_DEFAULT_SORT` 주석.
 */
const HISTORY_DEFAULT_SORT: TableSort = { key: 'ended', direction: 'desc' }

/** 소요 시간(ms) — 칸(`duration_text`)과 같은 두 칸의 뺄셈. 둘 중 하나라도 없으면 값 없음(맨 뒤) */
function durationMs(row: BatchListRow): number | null {
  if (row.started_at === null || row.completed_at === null) return null
  const ms = Date.parse(row.completed_at) - Date.parse(row.started_at)
  return Number.isFinite(ms) ? ms : null
}

export interface HistoryScreenProps {
  /**
   * 열린 운영 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`ops/opsTabs.ts`).
   * ⚠ **넘기지 않으면 탭 줄을 그리지 않는다.** 태블릿 kiosk 가 이 화면을 그대로 싣는데
   *   (`routes/tabletRoutes.tsx`) 거기서 콘솔 경로로 이동하면 kiosk 밖으로 튕겨 나간다.
   *   준비·관리 무리와 달리 **운영 3종만 태블릿과 겹쳐서** 이 갈래가 필요하다.
   */
  opsTab?: OpsTabId | undefined

  rows?: readonly BatchListRow[]
  /** 오른쪽 판에 열린 작업. 그 행이 «고른 행»이다(채움 + `aria-selected`) */
  selectedBatchId?: string | undefined
  access?: ScreenAccess
  loading?: boolean
  /**
   * 목록을 **못 본 까닭**. 🔴 이게 없어서 조회 실패가 「작업 없음」으로 나갔다(2026-09-22).
   *    `rows` 가 비었다는 사실과 **목록을 받지 못했다**는 사실은 다르다.
   */
  notice?: string | undefined
  /** 사유의 성격. 통신 실패·5xx 는 `failed`, 권한·잠김은 기본값 */
  noticeTone?: StateNoticeTone | undefined
  /** 행 클릭 → 작업 상세. 돌아가는 줄은 「← 이력」이 된다(`cur.bfrom` · §3-3) */
  onOpenBatch?: (batchId: string) => void
  /** 작업자 이름표. 🔴 **넘겼을 때만 「작업자」 열이 선다**(admin 전용 · `batchOwnerColumn.tsx` 머리말) */
  owners?: BatchOwners | undefined
  /**
   * 쪽 나눔(eric 2026-09-30 · `screens/listPaging.ts`). 🔴 **넘겼을 때만 쪽 넘김이 선다.** 검색이 바뀌면 1쪽.
   */
  paging?: ListPaging | undefined
}

export function HistoryScreen({
  opsTab,
  rows = DEMO_BATCH_HISTORY,
  selectedBatchId,
  access = {},
  loading = false,
  notice,
  noticeTone,
  onOpenBatch,
  owners,
  paging,
}: HistoryScreenProps) {
  const [query, setQueryState] = useState('')
  // 🔴 거름이 바뀌면 1쪽이다
  const setQuery = (next: string): void => {
    setQueryState(next)
    paging?.onChange(1)
  }

  // 🔴 「작업」 무리의 탭 줄. 이력은 자체 탭이 없어 **한 줄만** 그린다
  const groupTabs = useGroupTabLayout(OPS_TABS, opsTab, 'history', access)
  // 이 탭에 필요한 권한이 없으면 내용 대신 사유만 낸다(아래 본문 갈래). 사유 문자열 또는 null
  const viewBlocked = gate(access, { perm: 'view' }).disabledReason ?? null

  /*
   * 🔴 **검색은 «받은 쪽» 안에서만 걸린다** — `GET /batches` 에 검색 인자가 없다(2026-09-30 `openapi.json`).
   *    검색칸 옆 안내는 eric 지시로 뺐다(2026-09-30 「이거 빼줘」) — 이 사실은 주석에만 남는다.
   */
  const needle = query.trim().toLowerCase()
  const multiPage = paging !== undefined && paging.pageCount > 1
  const shown = rows.filter((row) =>
    needle === ''
      ? true
      : [row.batch_id, row.recipe_name, ...ownerSearchTexts(owners, row.created_by)].some((v) =>
          v.toLowerCase().includes(needle),
        ),
  )

  /*
   * 🔴 **「시료 수」는 2026-09-26 에 되살렸다** — 작업 목록과 같은 열 정의(`batchSampleCountColumn.tsx`)다.
   * 🔴 **「작업자」는 admin 에게만 이름으로 선다**(eric 2026-09-30 · `owners` 를 받았을 때만 · `batchOwnerColumn.tsx` 머리말).
   */
  /*
   * 🔴 **열 차례: 작업 ID → 상태(종전 「결과」) → 시료 수 → 레시피 → (작업자 · admin 만) → 생성 → 시작 → 종료 → 소요 시간**
   *    (eric 2026-09-30). 작업 목록과 차례가 다르다 — 이력은 상태(완료 · 중지)가 먼저 읽힌다.
   *    시각 셋은 eric 「작업 생성시간 종료시간 완료시간은 리스트에 포함시켜줘」(2026-09-30)다. 응답 시각 칸은
   *    `created_at` · `started_at` · `completed_at` 셋뿐이라 그 셋으로 넣었다 — 「종료」 머리글의 까닭은 `batchEndedColumn`.
   *    빠지는 차례는 열 차례와 따로 `listColumnPriority.ts` 가 정한다.
   * 🔴 **첫 열은 작업 ID 다**(eric 2026-09-22 「작업 데이터인데 작업 ID 가 가장 먼저 와야지.
   *    그 다음이 레시피고」). 「그 다음이 레시피」는 2026-09-30 에 위 차례로 바뀌었다(이력 한정).
   * 🔴 **행을 여는 링크도 첫 칸이다.** 첫 칸이 진짜 `button` 이어야 키보드로 갈 수 있다(§11-4-7).
   *    ID 는 넉넉하면 36자 그대로, 서랍이 열려 모자랄 때만 앞 8자다(eric 2026-09-28 「줄여」 · `batchIdColumn`).
   */
  const columns: Column<BatchListRow>[] = [
    // 🔴 첫 열 · 폭이 모자라면 앞 8자로 준다(eric 2026-09-28 「줄여」) — `batchIdColumn` 머리말
    batchIdColumn(onOpenBatch),
    /*
     * 🔴 **머리글은 「상태」다**(eric 2026-09-30 「결과 → 상태」). 값은 종전 그대로 `Batch.status`(완료 · 중지).
     *    `column.result` 는 다른 표(분주 결과 · 진행 결과 등)가 다른 뜻으로 쓰니 그 값을 바꾸지 않았다.
     *    진행 중 목록과 **같은 열 정의**다(`batchStatusColumn.tsx`) — 이력은 멈춘 까닭 없이 알약 하나
     */
    batchStatusColumn(),
    batchSampleCountColumn(),
    {
      key: 'recipe',
      header: t('column.recipe'),
      /*
       * 🔴 **한 줄로 세운다**(eric 5178 실측 2026-09-22). 서랍이 열린 폭(`.splitpage--open` 의
       *    5/11)에서 `fe-20260922-full-r1` 같은 이름이 **세 줄로 꺾여** 행이 높아졌다.
       *    넘치면 표가 가로로 스크롤하는 것이 맞다 — 잘라 숨기는 것만 금지다(작업 ID 와 같은 규칙).
       */
      nowrap: true,
      sort: 'text',
      sortValue: (row) => row.recipe_name,
      cell: (row) => <span>{row.recipe_name}</span>,
    },
    // 🔴 admin 에게만(`owners` 를 받았을 때만) 선다
    ...(owners === undefined ? [] : [batchOwnerColumn(owners)]),
    // 🔴 작업 목록과 **같은 열 정의**다(`batchTimeColumns.tsx`)
    batchCreatedColumn(),
    batchStartedColumn(),
    batchEndedColumn(),
    {
      key: 'duration',
      /*
       * 🔴 **소요 시간이다. 시작 «시각»이 아니다.** 2026-09-20 이전에는 `started_text` 를
       *    그려서 「기간」 헤더 아래에 시각이 찍혔다 — 라벨과 값이 달랐다. 되돌리지 마라.
       *    값은 `started_at`·`completed_at` 뺄셈이고 종착 전에는 `—` 다(`durationText()`).
       */
      header: t('column.duration'),
      nowrap: true,
      width: 'fit',
      sort: 'number',
      sortValue: durationMs,
      cell: (row) => <span className="muted mono">{row.duration_text}</span>,
    },
  ]

  return (
    <ListPage
      {...(groupTabs
        ? { onTabChange: groupTabs.onChange, tabValue: groupTabs.value, tabs: groupTabs.tabs }
        : {})}
      /* 무리 탭이 있으면 제목도 무리 이름이다(준비·관리와 같은 규칙). 태블릿은 화면 제목 그대로 */
      title={groupTabs ? t('screen.opsGroup.title') : t('screen.batchHistory.title')}
      toolbar={
        <Toolbar label={t('screen.batchHistory.title')}>
          <SearchInput
            label={t(
              owners === undefined ? 'filter.search.batches' : 'filter.search.batchesWithOwner',
            )}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
    >
      {/*
        🔴 **무리 안에서는 이 탭의 권한을 내용에도 건다**(2026-09-18). 이력·알람이 「작업」으로
           들어오면서 사이드바 문이 `monitor` 로 내려갔다 — 그래서 `view` 없는 역할(`service`)도
           이 주소로 **들어올 수는 있다.** 탭만 잠그고 내용을 내주면 막은 것이 아니다.
           탭 줄은 남긴다 — 알람 탭으로 갈 길이 필요하다.
        ⚠ 태블릿(무리 탭 없음)에서는 이 갈래가 돌지 않는다. 그쪽은 예전과 같다.
        ⚠ **집행은 서버 403 `INSUFFICIENT_ROLE`** 이다. 여기서 막았다고 안전해지는 것이 아니다.
      */}
      {groupTabs && viewBlocked ? (
        <p className="deny" role="status">
          {viewBlocked}
        </p>
      ) : (
        <>
          <DataTable
            caption={t('screen.batchHistory.title')}
            /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
            columns={withPriority('history', columns)}
            /*
             * 🔴 **남는 폭은 모든 열이 똑같이 나눈다**(eric 2026-09-30 「레시피 부분의 뒤가 많이 비고.. 전반적으로
             *    안맞아」). 종전에는 레시피 하나(`fill`)가 다 받아 그 뒤만 비었다. 실측은 `DataTable.columnWidths` 머리말
             */
            columnWidths="even"
            defaultSort={HISTORY_DEFAULT_SORT}
            emptyLabel={t('state.empty.batches')}
            loading={loading}
            {...(notice === undefined ? {} : { notice })}
            {...(noticeTone === undefined ? {} : { noticeTone })}
            /*
             * 🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23).
             */
            onRowClick={onOpenBatch === undefined ? undefined : (row) => onOpenBatch(row.batch_id)}
            rowKey={(row) => row.batch_id}
            rows={shown}
            selected={(row) => row.batch_id === selectedBatchId}
            /* 🔴 「N건」은 **전체 건수**(`total_count`)다. 검색으로 이 쪽 안을 걸렀으면 걸린 행 수다 */
            {...(needle !== '' || paging?.totalCount == null
              ? {}
              : { rowCount: paging.totalCount })}
          />
          {/* 🔴 한 쪽뿐이면 쪽 조작이 없다 */}
          {multiPage ? (
            <Pagination
              label={t('screen.batchHistory.title')}
              onChange={paging.onChange}
              page={paging.page}
              pageCount={paging.pageCount}
            />
          ) : null}
        </>
      )}
    </ListPage>
  )
}
